import React from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { capitalize } from 'lodash';
import { RATINGS } from '../../../lib/schema.js';
import RatingLabel from '../../RatingLabel.jsx';
import CalendarIcon from '../../icons/CalendarIcon.jsx';
import { validateEditorData } from '../../editor/defaultOptions.js';
import { StakeholderProfileLink } from '../../links/index.jsx';

import Editor from '../../editor/editor.jsx';

const StakeholdersHistoryModal = ({ history, show, onHide, project }) => {
  const fieldInfluence = (
    project.stakeholderFields.find(
      (field) => field.nativeColumn === 'influence'
    ) || { name: '' }
  ).name;
  const fieldInterest = (
    project.stakeholderFields.find(
      (field) => field.nativeColumn === 'interest'
    ) || { name: '' }
  ).name;
  const fieldImpact = (
    project.stakeholderFields.find(
      (field) => field.nativeColumn === 'impact'
    ) || { name: '' }
  ).name;
  const fieldEngagementFrequency = (
    project.stakeholderFields.find(
      (field) => field.nativeColumn === 'engagement_frequency'
    ) || { name: '' }
  ).name;
  const fieldSupportForChange = (
    project.stakeholderFields.find(
      (field) => field.nativeColumn === 'support'
    ) || { name: '' }
  ).name;

  const sortedData = () =>
    // newest first
    history.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      if (dateA > dateB) {
        return -1; // Return a negative value to sort A before B
      }
      if (dateA < dateB) {
        return 1; // Return a positive value to sort B before A
      }
      return 0; // Dates are equal, compare the time
    });

  const renderColors = (key, reverse) => {
    if (RATINGS.HIGH_MEDIUM_LOW.includes(key)) {
      return (
        <span key={key}>
          <RatingLabel reverse={reverse} rating={key} />
        </span>
      );
    }
    if (RATINGS.FREQUENCY_LEVELS.includes(key)) {
      return (
        <span key={key}>
          <RatingLabel reverse={reverse} rating={key} />
        </span>
      );
    }
    if (RATINGS.SUPPORT_LEVELS.includes(key)) {
      return (
        <span key={key}>
          <RatingLabel reverse={reverse} rating={key} />
        </span>
      );
    }
  };

  const renderChangedNames = (item) => {
    if (item.change_names?.length) {
      return item.change_names
        .reduce((acc, val, i) => {
          const index = Math.floor(i / 2);
          acc[index] = acc[index] || [];
          acc[index].push(val);
          return acc;
        }, [])
        .map((group, index) => {
          return (
            <Row className="m-b-12" key={index}>
              <Col xs={4} className="text-muted  modal-key-label">
                {index === 0 ? 'Stakeholder Analysis update:' : ''}
              </Col>
              <Col xs={8}>
                {group.map((item, index) => (
                  <span key={index}>
                    {item}
                    {index < group.length - 1 ? ' → ' : ''}
                  </span>
                ))}
              </Col>
            </Row>
          );
        });
    }
  };

  const columnAdded = (item) => {
    if (item.new_columns?.length) {
      const addedColumns = item.new_columns.join(', ');
      return <span className="m-r-8">{capitalize(addedColumns)}</span>;
    }
  };

  const columnDeleted = (item) => {
    return item.deleted_columns.map((deleted, index) => (
      <span key={index} className="m-r-8">
        {capitalize(deleted)}
        {item.deleted_columns.length > 1 &&
        index !== item.deleted_columns.length - 1
          ? ','
          : ''}
      </span>
    ));
  };

  const showDefaultColumnNames = (name) => {
    switch (name) {
      case 'influence':
        return 'Level of influence';
      case 'interest':
        return 'Level of interest';
      case 'impact':
        return 'Predicted impact';
      default:
        return name;
    }
  };

  const renderField = (name, before, after, reverse) => {
    return (
      <Row className="m-b-12">
        <Col xs={4} className="text-muted  modal-key-label">
          {showDefaultColumnNames(name)}
        </Col>
        <Col xs={8}>
          {renderColors(before, reverse)}
          <span className="m-l-16 m-r-16">→</span>
          {renderColors(after, reverse)}
        </Col>
      </Row>
    );
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Version History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {sortedData().map((item) => {
          const beforeInfluence =
            item[fieldInfluence] && item[fieldInfluence][0];
          const afterInfluence =
            item[fieldInfluence] && item[fieldInfluence][1];

          const beforeInterest = item[fieldInterest] && item[fieldInterest][0];
          const afterInterest = item[fieldInterest] && item[fieldInterest][1];

          const beforeImpact = item[fieldImpact] && item[fieldImpact][0];
          const afterImpact = item[fieldImpact] && item[fieldImpact][1];

          const beforeEngagementFrequency =
            item[fieldEngagementFrequency] && item[fieldEngagementFrequency][0];
          const afterEngagementFrequency =
            item[fieldEngagementFrequency] && item[fieldEngagementFrequency][1];

          const beforeSupportForChange =
            item[fieldSupportForChange] && item[fieldSupportForChange][0];
          const afterSupportForChange =
            item[fieldSupportForChange] && item[fieldSupportForChange][1];

          return (
            <div key={item.time}>
              {item.type === 'stakeholder' ? (
                <div className="m-b-32">
                  <div className="flex m-b-14">
                    <CalendarIcon />
                    <div>
                      <span className="m-l-12 m-r-8">{item.time}</span>
                      <strong>
                        <span>{item.date}</span>
                      </strong>
                    </div>
                  </div>

                  <div className="m-l-38">
                    <Row className="m-b-12">
                      <Col xs={4} className="text-muted modal-key-label">
                        Stakeholder:
                      </Col>
                      <Col xs={8}>
                        <StakeholderProfileLink
                          projectId={project.id}
                          stakeholderId={item.stakeholder_id}
                        >
                          {capitalize(item.stakeholder)}
                        </StakeholderProfileLink>{' '}
                        &nbsp;
                        <span className="text-muted">
                          was &nbsp;
                          {item.event === 'create' ? 'created' : 'edited'}
                        </span>
                      </Col>
                    </Row>

                    {item[fieldInfluence]
                      ? renderField(
                          fieldInfluence,
                          beforeInfluence,
                          afterInfluence
                        )
                      : null}

                    {item[fieldInterest]
                      ? renderField(
                          fieldInterest,
                          beforeInterest,
                          afterInterest
                        )
                      : null}

                    {item[fieldImpact]
                      ? renderField(
                          fieldImpact,
                          beforeImpact,
                          afterImpact,
                          true
                        )
                      : null}

                    {item[fieldEngagementFrequency]
                      ? renderField(
                          fieldEngagementFrequency,
                          beforeEngagementFrequency,
                          afterEngagementFrequency
                        )
                      : null}

                    {item[fieldSupportForChange]
                      ? renderField(
                          fieldSupportForChange,
                          beforeSupportForChange,
                          afterSupportForChange
                        )
                      : null}

                    {item.type === 'stakeholder' && item.comment ? (
                      <Row className=" flex-align-baseline">
                        <Col
                          xs={4}
                          style={{
                            lineHeight: '1.6em',
                            paddingTop: '0.4em',
                            paddingBottom: '0.4em',
                          }}
                          className="text-muted modal-key-label"
                        >
                          <span>Comment:</span>
                        </Col>
                        <Col xs={8}>
                          <Editor
                            customClass="m-b-0"
                            options={{
                              holder: `content-${item.id}-editor`,
                              readOnly: true,
                              minHeight: 0,
                            }}
                            data={validateEditorData(item.comment)}
                          />
                        </Col>
                      </Row>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {item.type === 'customization' ? (
                <div className="m-b-32">
                  <div className="flex m-b-14">
                    <CalendarIcon />
                    <div>
                      <span className="m-l-12 m-r-8">{item.time}</span>
                      <strong>
                        <span>{item.date}</span>
                      </strong>
                    </div>
                  </div>

                  <div className="m-l-38 m-r-38">
                    {item.change_names?.length > 0 && true
                      ? renderChangedNames(item)
                      : null}

                    {item.new_columns?.length > 0 ? (
                      <Row className=" m-b-12">
                        <Col xs={4} className="text-muted modal-key-label">
                          Column Added:
                        </Col>
                        <Col xs={8}>{columnAdded(item)}</Col>
                      </Row>
                    ) : null}

                    {item.deleted_columns &&
                    item.deleted_columns.length > 0 &&
                    true ? (
                      <Row className=" m-b-12">
                        <Col xs={4} className="text-muted modal-key-label">
                          Column Deleted:
                        </Col>
                        <Col xs={8}>{columnDeleted(item)}</Col>
                      </Row>
                    ) : null}
                    {item.type === 'customization' && item.comment !== null ? (
                      <Row className="m-b-12">
                        <Col
                          xs={4}
                          style={{
                            lineHeight: '1.6em',
                            paddingTop: '0.4em',
                            paddingBottom: '0.4em',
                          }}
                          className="text-muted modal-key-label"
                        >
                          <span>Comment:</span>
                        </Col>
                        <Col xs={8}>
                          <Editor
                            customClass="m-b-0"
                            options={{
                              holder: `content-${item.id}-editor`,
                              readOnly: true,
                              minHeight: 0,
                            }}
                            data={validateEditorData(item.comment)}
                          />
                        </Col>
                      </Row>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </Modal.Body>
      <Modal.Footer></Modal.Footer>
    </Modal>
  );
};

export default StakeholdersHistoryModal;
